// Client-only "view as role" override for internal testing of role gating.
//
// Lets an eligible internal user (see useRole's canOverrideRole) temporarily view
// the workspace admin AS a given role — photographer/salesperson/administrator/owner
// — so they can verify what each role can see and do. It ONLY changes client-side
// gating (what useRole reports); the backend still authorises every request by the
// real token + role, so this can never escalate real access. It's a testing lens.
//
// Backed by localStorage so the choice survives navigation and reloads, and exposed
// through an external store so useSyncExternalStore keeps every useRole() consumer in
// sync the instant the role is switched — no page reload, no prop threading.

import type { WorkspaceRole } from "@/hooks/useRole";

const KEY = "ph_role_override";
const VALID: WorkspaceRole[] = ["owner", "administrator", "photographer", "salesperson"];

const listeners = new Set<() => void>();

/** Current override, or null when none is set (or on the server). */
export const getRoleOverride = (): WorkspaceRole | null => {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(KEY);
    return v && (VALID as string[]).includes(v) ? (v as WorkspaceRole) : null;
  } catch {
    return null;
  }
};

/** Server snapshot is always null so SSR renders the real role (no override). */
export const getRoleOverrideServer = (): WorkspaceRole | null => null;

/** Set (or clear, with null) the override and notify all subscribers. */
export const setRoleOverride = (role: WorkspaceRole | null): void => {
  if (typeof window === "undefined") return;
  try {
    if (role && (VALID as string[]).includes(role)) window.localStorage.setItem(KEY, role);
    else window.localStorage.removeItem(KEY);
  } catch {
    /* ignore storage failures — the override is a best-effort testing aid */
  }
  listeners.forEach((l) => l());
};

export const subscribeRoleOverride = (cb: () => void): (() => void) => {
  listeners.add(cb);
  // Reflect changes made in another tab.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) cb();
  };
  if (typeof window !== "undefined") window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
  };
};
